import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const choices = [
  { id: "rock", label: "Rock", emoji: "🪨" },
  { id: "paper", label: "Paper", emoji: "📄" },
  { id: "scissors", label: "Scissors", emoji: "✂️" },
];

const getOutcome = (player, computer) => {
  if (player === computer) return "tie";
  if (
    (player === "rock" && computer === "scissors") ||
    (player === "paper" && computer === "rock") ||
    (player === "scissors" && computer === "paper")
  ) {
    return "win";
  }
  return "lose";
};

const Container = styled.div`
  min-height: 100vh;
  padding: 40px 20px 70px;
  background: radial-gradient(circle at top right, rgba(236, 72, 153, 0.18), transparent 28%),
    linear-gradient(180deg, #050918 0%, #101840 100%);
  color: ${({ theme }) => theme.text_primary};
`;

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 42px;
  margin: 0;
  color: ${({ theme }) => theme.primary};
`;

const Description = styled.p`
  line-height: 1.8;
  color: ${({ theme }) => theme.text_secondary};
`;

const NameForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const Input = styled.input`
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
  min-width: 240px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Button = styled.button`
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  background: ${({ color }) => color || "#6366f1"};
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

const ResultCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 26px;
  display: grid;
  gap: 16px;
`;

const ResultTitle = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
`;

const ResultText = styled.div`
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};
`;

const ChoiceBlock = styled.div`
  display: grid;
  gap: 12px;
`;

const ChoiceCard = styled.div`
  padding: 18px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg};
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChoiceLabel = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Scoreboard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  @media only screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  flex: 1;
  min-width: 220px;
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 18px;
`;

const InfoTitle = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
`;

const InfoValue = styled.div`
  font-size: 20px;
  font-weight: 700;
`;

const RockPaperScissors = () => {
  const [playerName, setPlayerName] = useState("");
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState(null);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [ties, setTies] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleStart = () => {
    if (playerName.trim()) {
      setStarted(true);
      setResult(null);
      setWins(0);
      setLosses(0);
      setTies(0);
    }
  };

  const handleChoice = (choice) => {
    if (!started) return;
    const computer = choices[Math.floor(Math.random() * choices.length)];
    const outcome = getOutcome(choice.id, computer.id);
    let message = "";
    if (outcome === "tie") {
      message = `Tie! Even the computers can't agree — ${choice.label} vs ${computer.label}. It's a full computer beat computer draw.`;
    } else if (outcome === "lose") {
      message = `Computer beat computer! ${computer.label} crushed ${choice.label}, and the machine is celebrating this round.`;
    } else {
      message = `Legendary win, ${playerName}! ${choice.label} beats ${computer.label} — the human beat the machine.`;
      setWins((value) => value + 1);
    }
    if (outcome === "lose") {
      setLosses((value) => value + 1);
    }
    if (outcome === "tie") {
      setTies((value) => value + 1);
    }
    setResult({ playerChoice: choice, computerChoice: computer, outcome, message });
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setResult(null);
    }, 4000);
  };

  return (
    <Container>
      <Wrapper>
        <Title>Rock Paper Scissors</Title>
        <Description>
          A playful React-powered Rock Paper Scissors experience. Tell us your name, choose your move, and see whether the machine folds, loses, or forces a computer beat computer tie. Every round has a colorful result with fun commentary.
        </Description>
        {!started ? (
          <NameForm>
            <Input
              placeholder="Your name here"
              value={playerName}
              onChange={(event) => setPlayerName(event.target.value)}
            />
            <Button onClick={handleStart}>Let's play</Button>
          </NameForm>
        ) : (
          <InfoRow>
            <InfoPanel>
              <InfoTitle>Player</InfoTitle>
              <InfoValue>{playerName}</InfoValue>
            </InfoPanel>
            <InfoPanel>
              <InfoTitle>Mode</InfoTitle>
              <InfoValue>Player vs Computer</InfoValue>
            </InfoPanel>
          </InfoRow>
        )}

        {started && (
          <>
            <Scoreboard>
              <InfoPanel>
                <InfoTitle>Wins</InfoTitle>
                <InfoValue>{wins}</InfoValue>
              </InfoPanel>
              <InfoPanel>
                <InfoTitle>Losses</InfoTitle>
                <InfoValue>{losses}</InfoValue>
              </InfoPanel>
              <InfoPanel>
                <InfoTitle>Ties</InfoTitle>
                <InfoValue>{ties}</InfoValue>
              </InfoPanel>
            </Scoreboard>
            <ButtonGroup>
              {choices.map((choice) => (
                <Button
                  key={choice.id}
                  onClick={() => handleChoice(choice)}
                  color={choice.id === "rock" ? "#6b7280" : choice.id === "paper" ? "#10b981" : "#f97316"}
                >
                  {choice.emoji} {choice.label}
                </Button>
              ))}
            </ButtonGroup>
          </>
        )}

        {result && (
          <ResultCard>
            <ResultTitle>{result.outcome === "tie" ? "Tie Game" : result.outcome === "win" ? "Victory!" : "Computer Wins"}</ResultTitle>
            <ResultText>{result.message}</ResultText>
            <ChoiceBlock>
              <ChoiceCard>
                <ChoiceLabel>{playerName}'s choice</ChoiceLabel>
                <InfoValue>{result.playerChoice.emoji} {result.playerChoice.label}</InfoValue>
              </ChoiceCard>
              <ChoiceCard>
                <ChoiceLabel>Computer choice</ChoiceLabel>
                <InfoValue>{result.computerChoice.emoji} {result.computerChoice.label}</InfoValue>
              </ChoiceCard>
            </ChoiceBlock>
          </ResultCard>
        )}
      </Wrapper>
    </Container>
  );
};

export default RockPaperScissors;
